import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { CtaBand } from "../ui";

/* --------------------------------------------------------
   포럼 공통 틀 — 왼쪽 메뉴 트리(분류 → 글 제목)는 목록을 보든 글을 읽든 그대로 둔다.
   PC: 왼쪽에 붙어 스크롤해도 따라온다.
   모바일: 맨 위 '포럼 메뉴' 버튼으로 펼치고, 글을 고르면 접힌다.
   -------------------------------------------------------- */

function Chevron({ open }) {
  return (
    <svg className={`forum-tree__chev ${open ? "is-open" : ""}`} viewBox="0 0 24 24" width="14" height="14"
         fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

export default function ForumLayout() {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sideRef = useRef(null);

  const [tree, setTree] = useState(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(() => new Set()); // 펼친 분류
  const [menu, setMenu] = useState(false);          // 모바일 메뉴 펼침

  const m = pathname.match(/^\/forum\/(\d+)/);
  const postId = m ? Number(m[1]) : null;
  const listCat = postId ? null : params.get("cat");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch("/api/forum/tree");
        if (!res.ok) throw new Error();
        const data = await res.json();
        if (alive) setTree(data.categories || []);
      } catch {
        if (alive) setFailed(true);
      }
    })();
    return () => { alive = false; };
  }, []);

  /* 지금 보고 있는 글(또는 분류)이 속한 분류를 펼친다 */
  const currentCat = useMemo(() => {
    if (!tree) return null;
    if (postId) return tree.find((c) => c.posts.some((p) => p.id === postId))?.id || null;
    return listCat;
  }, [tree, postId, listCat]);

  useEffect(() => {
    if (currentCat) setOpen((prev) => (prev.has(currentCat) ? prev : new Set(prev).add(currentCat)));
  }, [currentCat]);

  /* 페이지를 옮기면 모바일 메뉴는 닫는다 */
  useEffect(() => { setMenu(false); }, [pathname, listCat]);

  /* 메뉴가 길어도 지금 글이 보이도록 메뉴 안에서만 스크롤한다(페이지는 움직이지 않음) */
  useEffect(() => {
    const side = sideRef.current;
    const el = side?.querySelector(".forum-tree__post.is-current");
    if (!side || !el) return;
    const top = el.offsetTop;
    if (top < side.scrollTop || top > side.scrollTop + side.clientHeight - 40) {
      side.scrollTop = Math.max(0, top - side.clientHeight / 3);
    }
  }, [postId, tree, open]);

  /* 분류 이름: 접혀 있으면 펼치고 그 분류 목록으로, 이미 그 분류를 보고 있으면 접었다 폈다 */
  function onCategory(cid) {
    const viewing = !postId && listCat === cid;
    if (viewing) {
      setOpen((prev) => {
        const next = new Set(prev);
        if (next.has(cid)) next.delete(cid); else next.add(cid);
        return next;
      });
      return;
    }
    setOpen((prev) => new Set(prev).add(cid));
    navigate(`/forum?cat=${cid}`);
  }

  function toggle(cid) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid); else next.add(cid);
      return next;
    });
  }

  const currentLabel = postId
    ? tree?.flatMap((c) => c.posts).find((p) => p.id === postId)?.title
    : tree?.find((c) => c.id === listCat)?.label;

  return (
    <>
      <div className="forum-shell">
        <aside className={`forum-side ${menu ? "is-open" : ""}`} aria-label="포럼 메뉴">
          <button
            type="button"
            className="forum-side__toggle"
            aria-expanded={menu}
            onClick={() => setMenu((v) => !v)}
          >
            <span className="forum-side__toggle-k">포럼 메뉴</span>
            <span className="forum-side__toggle-v">{currentLabel || "전체 글"}</span>
            <Chevron open={menu} />
          </button>

          <nav ref={sideRef} className="forum-side__panel">
            <Link
              to="/forum"
              className={`forum-tree__home ${!postId && !listCat ? "is-current" : ""}`}
            >
              포럼 <span>전체 글</span>
            </Link>

            {failed && <p className="forum-tree__empty">메뉴를 불러오지 못했습니다.</p>}
            {!tree && !failed && <p className="forum-tree__empty">불러오는 중…</p>}

            {tree && (
              <ul className="forum-tree">
                {tree.map((c) => {
                  const isOpen = open.has(c.id);
                  return (
                    <li key={c.id} className={`forum-tree__cat ${currentCat === c.id ? "is-active" : ""}`}>
                      <div className="forum-tree__cat-row">
                        <button
                          type="button"
                          className="forum-tree__cat-btn"
                          aria-expanded={isOpen}
                          onClick={() => onCategory(c.id)}
                        >
                          <span>{c.label}</span>
                          <span className="forum-tree__count">{c.posts.length}</span>
                        </button>
                        <button
                          type="button"
                          className="forum-tree__fold"
                          aria-label={isOpen ? `${c.label} 접기` : `${c.label} 펼치기`}
                          onClick={() => toggle(c.id)}
                        >
                          <Chevron open={isOpen} />
                        </button>
                      </div>
                      {isOpen && (
                        c.posts.length === 0 ? (
                          <p className="forum-tree__empty forum-tree__empty--sub">아직 글이 없습니다.</p>
                        ) : (
                          <ul className="forum-tree__posts">
                            {c.posts.map((p) => (
                              <li key={p.id}>
                                <Link
                                  to={`/forum/${p.id}`}
                                  className={`forum-tree__post ${postId === p.id ? "is-current" : ""}`}
                                  aria-current={postId === p.id ? "page" : undefined}
                                  title={p.title}
                                >
                                  {p.pinned && <span className="forum-tree__pin">공지</span>}
                                  {p.title}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        )
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </nav>
        </aside>

        <div className="forum-main">
          <Outlet />
        </div>
      </div>

      <CtaBand
        title="찾으시는 내용이 없다면"
        desc="만들고 싶은 것이나 막힌 부분을 알려주시면 검토해 드립니다."
      />
    </>
  );
}
